import React from "react";
import ReportingChartMenu from "../../../ReportingChartMenu";
import { DateTime } from "luxon";
import { useReportingChartPeriod } from "../../../useReportingChartPeriod";
import { useReparabilityMatrixPeriod } from "../useReparabilityMatrixPeriod";
import { usePeriodComparisonOptions } from "../../../usePeriodComparison";
import type { DiagnosisReporting } from "../../../../../../api/useReporting";

function PeriodSelector({ reporting }: { reporting: DiagnosisReporting }) {
  const periodComparisonOptions = usePeriodComparisonOptions(reporting.range);
  const [selectedReparabilityMatrixPeriod, setSelectedReparabilityMatrixPeriod] =
    useReparabilityMatrixPeriod({});
  const [chartPeriod, setChartPeriod] = useReportingChartPeriod(reporting, reporting.range);

  const setPeriod = React.useCallback(
    (value: React.SetStateAction<string>) => {
      const date = DateTime.fromISO(value as string);

      if (!date) return;

      const min = date.startOf("month");
      const max = date.endOf("month");

      setSelectedReparabilityMatrixPeriod(date.toMillis());
      setChartPeriod(() => ({
        from: min.toMillis(),
        to: max.toMillis(),
      }));
    },
    [setChartPeriod, setSelectedReparabilityMatrixPeriod],
  );

  const selectedPeriod = React.useMemo(() => {
    if (selectedReparabilityMatrixPeriod)
      return DateTime.fromMillis(selectedReparabilityMatrixPeriod).toFormat("yyyy-MM");
    else return chartPeriod.from && DateTime.fromMillis(chartPeriod.from).toFormat("yyyy-MM");
  }, [chartPeriod.from, selectedReparabilityMatrixPeriod]);

  React.useEffect(() => {
    if (!selectedReparabilityMatrixPeriod && selectedPeriod) {
      setSelectedReparabilityMatrixPeriod(DateTime.fromISO(selectedPeriod).toMillis());
    }
  }, [selectedPeriod, selectedReparabilityMatrixPeriod, setSelectedReparabilityMatrixPeriod]);

  if (!selectedPeriod) return null;

  return (
    <ReportingChartMenu
      options={periodComparisonOptions}
      currentValue={selectedPeriod}
      setCurrentValue={setPeriod}
    />
  );
}

export default React.memo(PeriodSelector);
